import { ReactNode, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useQueryUsers } from "Hooks/tenants/useQueryUsers";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";
import OptionCustom from "Components/UI/NewFormGroup/OptionImg";
import { getTheme } from "Store/MultiDomain/MultiDomain.selector";
import {
  arrayActionKeys,
  arrayNotificationKeysPtBr,
  FilterOperationType,
  IFiltroOptions,
  IUpdateFilter,
  NotificationFilterTypeKeys,
} from "Data/interfaces/Notification/IFilters";
import { optionsDefaultOperationsObj } from "./helper";

interface IUseFilterDropdown {
  id: string;
  item?: IFiltroOptions;
  search?: string;
  updateFilter: (values: IUpdateFilter) => void;
}

export interface ICheckOptions {
  label: ReactNode | string;
  value: any;
  description: any;
}

const useFilterDropdown = ({
  id,
  item,
  search,
  updateFilter,
}: IUseFilterDropdown) => {
  const { t } = useTranslation('notification');
  const { data: listUsers } = useQueryUsers();

  const theme = useSelector(getTheme);
  const obrasList = useSelector((state: IGlobalReducerState) => state.obra.ObrasList);

  const checkOptions = useMemo(() => {
    let options: ICheckOptions[] = [];

    const checkOptionsObj: Record<string, () => ICheckOptions[]> = {
      authorIds: () => {
        const opts: ICheckOptions[] = listUsers
          .filter(user => {
            return user.Nome?.toLowerCase()?.includes(search?.toLowerCase() || '') ||
              user.Email?.toLowerCase()?.includes(search?.toLowerCase() || '');
          })
          .sort((a, b) => a?.Nome?.localeCompare(b?.Nome || '') || 0)
          .map(user => ({
            label: <OptionCustom
              label={user.Nome || user.Email || ''}
              avatar
              src={user.OriginalImageUrl || ''}
              thumbSrc={user.ThumbnailImageUrl}
              thumbType="small"
            />,
            value: user.Id,
            description: {
              name: user.Nome,
              img: user.OriginalImageUrl || user.ThumbnailImageUrl,
            },
          }));
        return opts;
      },
      actions: () => {
        const opts: ICheckOptions[] = arrayActionKeys
          .filter(key => arrayNotificationKeysPtBr[key]?.toLowerCase()?.includes(search?.toLowerCase() || ''))
          .sort((a, b) => arrayNotificationKeysPtBr[a]?.localeCompare(arrayNotificationKeysPtBr[b]) || 0)
          .map(key => ({
            label: arrayNotificationKeysPtBr[key],
            value: key,
            description: arrayNotificationKeysPtBr[key],
          }));
        return opts;
      },
      csIds: () => {
        const opts: ICheckOptions[] = obrasList
          ?.filter(obra => obra.Name?.toLowerCase()?.includes(search?.toLowerCase() || ''))
          ?.sort((a, b) => a?.Name?.localeCompare(b?.Name || '') || 0)
          ?.map(obra => ({
            label: <OptionCustom
              label={obra.Name || ''}
              avatar
              src={obra.OriginalImageUrl || ''}
              thumbSrc={obra.ThumbnailImageUrl}
              thumbType="small"
            />,
            value: obra.ConstructionSiteId,
            description: {
              name: obra.Name,
              img: obra.OriginalImageUrl || obra.ThumbnailImageUrl,
            },
          })) || [];
        return opts;
      },
      isRead: () => {
        const opts: ICheckOptions[] = [
          {
            label: t('filters.unread'),
            value: false,
            description: t('filters.unread'),
          },
          {
            label: t('filters.read'),
            value: true,
            description: t('filters.read'),
          },
        ];
        return opts;
      },
    }

    if (item?.key && checkOptionsObj.hasOwnProperty(item.key)) {
      options = checkOptionsObj[item.key]();
    }

    return {
      checkOptionsObj,
      options,
    };
  }, [
    item?.key,
    obrasList,
    listUsers,
    search,
  ]);

  const hasCheck = useMemo(() => checkOptions.options.length > 0, [checkOptions]);

  const handleKeyUpdateFilter = (selectValue: NotificationFilterTypeKeys) => {
    updateFilter({
      op: 'id',
      values: {
        id: id,
        key: selectValue,
      }
    });
  };

  const handleOperationUpdateFilter = (selectValue: FilterOperationType) => {
    updateFilter({
      op: 'op',
      values: {
        id: id,
        operation: selectValue,
      }
    });
  };

  const handleValueUpdateFilter = (selectValue: any, override?: boolean) => {
    if (hasCheck) {
      if (item?.values) {
        let values: any = item.values;
        if (override) {
          values = [selectValue];
        }
        if (!override) {
          if (values.includes(selectValue)) {
            values = values.filter((vl: any) => vl !== selectValue);
          } else {
            values.push(selectValue);
          }
        }
        const filteredDescription = checkOptions.checkOptionsObj[item.key]()
          .filter(opt => {
            return values.includes(opt.value);
          });
        const description = filteredDescription.map(description => description.description);
        return updateFilter({
          op: 'vl',
          values: {
            id: id,
            values,
            description,
          }
        });
      }

      const description = checkOptions.checkOptionsObj[item?.key || '']()
        .find(opt => {
          return selectValue === opt.value;
        })?.description;
      return updateFilter({
        op: 'vl',
        values: {
          id: id,
          values: [selectValue],
          description: [description],
        }
      });
    }

    return updateFilter({
      op: 'vl',
      values: {
        id: id,
        values: selectValue,
        description: selectValue,
      }
    });
  };

  useEffect(() => {
    if (
      item?.key &&
      !item?.values &&
      !['empty', 'notEmpty'].includes(item.operation)
    ) {
      handleOperationUpdateFilter(optionsDefaultOperationsObj[item?.key]);
    }
  }, [item?.key, item?.values]);

  return {
    checkOptions,
    hasCheck,
    handleKeyUpdateFilter,
    handleOperationUpdateFilter,
    handleValueUpdateFilter,
    theme,
  }
}

export default useFilterDropdown;
